/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { useTranslation } from 'react-i18next';
import { Eye, History, Table2, Upload } from 'lucide-react';

import { Text } from '@/shared/ui';
import { formatLocalizedDate } from '@/shared/utils/date';

import type { LaunchMaterial, PriceTableOverview, PriceTableVersion } from '../../types';
import { MaterialTitle } from './MaterialCard';

/*
 * Authored inline, as the prototype authors it: a freshly written arbitrary
 * Tailwind class is not reliably generated in this project.
 */

/** Day/month only: the year is noise on a table published this season. */
function shortDate(iso?: string | null): string {
  if (!iso) return '';
  return formatLocalizedDate(iso, undefined, { day: '2-digit', month: '2-digit' });
}

/** Human size, in the unit a person would say out loud. */
function formatSize(bytes?: number | null): string {
  if (!bytes) return '';
  const mb = bytes / (1024 * 1024);
  return mb >= 1 ? `${mb.toFixed(1)} MB` : `${Math.round(bytes / 1024)} KB`;
}

/**
 * The warning a broker needs before quoting a price.
 *
 * Shown only when this user has not opened the version in force. It is the
 * single most valuable thing on the materials screen: negotiating from a
 * superseded table is the mistake this whole area exists to prevent — so it
 * sits above everything, in one line, with the way out on the right.
 */
export function StalePriceBanner({ overview, onOpenCurrent }: {
  overview: PriceTableOverview;
  onOpenCurrent: () => void;
}) {
  const { t } = useTranslation('features/launches');
  if (!overview.has_newer || !overview.current) return null;

  return (
    <div
      data-testid="stale-price-banner"
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 'var(--space-3)',
        padding: 'var(--space-3) var(--space-4)',
        borderRadius: 'var(--radius-md)',
        background: 'var(--launch-price-stale-bg)',
        border: '1px solid var(--launch-price-stale-border)',
        color: 'var(--launch-price-stale-fg)',
      }}
    >
      <Table2 size={18} style={{ flexShrink: 0 }} />

      <Text as="span" style={{ flex: 1, minWidth: 0, fontSize: 'var(--text-body-sm)' }}>
        {overview.seen_version
          ? t('priceTable.staleWithSeen', {
              version: overview.current.version,
              from: shortDate(overview.current.valid_from),
              seen: overview.seen_version,
            })
          : t('priceTable.staleNeverOpened', {
              version: overview.current.version,
              from: shortDate(overview.current.valid_from),
            })}
      </Text>

      <button
        type="button"
        onClick={onOpenCurrent}
        style={{
          height: 'var(--space-9)',
          padding: '0 var(--space-4)',
          borderRadius: 'var(--radius-md)',
          border: 'none',
          background: 'var(--button-primary-bg)',
          color: 'var(--button-primary-text)',
          fontSize: 'var(--text-small)',
          fontWeight: 'var(--font-semibold)',
          cursor: 'pointer',
          whiteSpace: 'nowrap',
          flexShrink: 0,
        }}
      >
        {t('priceTable.openCurrent', { version: overview.current.version })}
      </button>
    </div>
  );
}

/** One label-over-value cell in the current version's metadata row. */
function Fact({ label, value }: { label: string; value: string }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-0-5)', minWidth: 0 }}>
      <Text as="span" style={{ fontSize: 'var(--text-meta)', color: 'var(--text-tertiary)' }}>
        {label}
      </Text>
      <Text
        as="span"
        style={{
          fontSize: 'var(--text-body)',
          fontWeight: 'var(--font-semibold)',
          color: 'var(--text-primary)',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        {value}
      </Text>
    </div>
  );
}

/** One archived version, as a row in the history list. */
function ArchivedRow({ version, name, size, seen, onOpen, t }: {
  version: PriceTableVersion;
  /** The name the manager gave this table, when its material still exists. */
  name: string;
  size: string;
  seen: boolean;
  onOpen: () => void;
  t: (key: string, options?: Record<string, unknown>) => string;
}) {
  return (
    <button
      type="button"
      onClick={onOpen}
      data-testid="price-table-archived-row"
      style={{
        width: '100%',
        minHeight: 'var(--space-14)',
        display: 'flex',
        alignItems: 'center',
        gap: 'var(--space-3)',
        padding: 'var(--space-2) var(--space-3)',
        border: 'none',
        borderBottom: '1px solid var(--border-subtle)',
        background: 'transparent',
        textAlign: 'left',
        color: 'var(--text-primary)',
        cursor: 'pointer',
      }}
    >
      <span
        style={{
          width: 'var(--space-10)',
          height: 'var(--space-10)',
          flexShrink: 0,
          borderRadius: 'var(--radius-md)',
          background: 'var(--surface-tertiary)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 'var(--text-body-sm)',
          fontWeight: 'var(--font-bold)',
          color: 'var(--text-secondary)',
        }}
      >
        {`v${version.version}`}
      </span>

      <span style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
        <Text
          as="span"
          style={{
            fontSize: 'var(--text-body-sm)',
            fontWeight: 'var(--font-medium)',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {name || t('priceTable.range', {
            from: shortDate(version.valid_from),
            to: shortDate(version.valid_to),
          })}
        </Text>
        <Text as="span" style={{ fontSize: 'var(--text-meta)', color: 'var(--text-tertiary)' }}>
          {[
            t('priceTable.range', {
              from: shortDate(version.valid_from),
              to: shortDate(version.valid_to),
            }),
            size ? t('priceTable.archivedWithSize', { size }) : t('priceTable.archived'),
          ].join(' · ')}
        </Text>
      </span>

      {seen && (
        <Text
          as="span"
          style={{
            fontSize: 'var(--text-tag)',
            fontWeight: 'var(--font-semibold)',
            padding: 'var(--space-1) var(--space-1-5)',
            borderRadius: 'var(--radius-sm)',
            background: 'var(--launch-price-stale-bg)',
            color: 'var(--launch-price-stale-fg)',
            flexShrink: 0,
          }}
        >
          {t('priceTable.lastYouOpenedTag')}
        </Text>
      )}

      <History size={16} color="var(--text-tertiary)" style={{ flexShrink: 0 }} />
    </button>
  );
}

/**
 * The price table as its own object: the version in force on the left, with
 * what a broker checks before quoting — since when, by whom, which file —
 * and the archive beside it, marked plainly as not for negotiating.
 *
 * The version in force is a neutral card with a "Vigente" pill rather than
 * a wholly tinted panel: the tint was carrying the meaning, which left the
 * archived rows looking like the same kind of object in a duller colour.
 */
export function PriceTablePanel({
  overview,
  onOpenVersion,
  materialFor,
  canManage = false,
  onPublishNew,
  onRename,
}: {
  overview: PriceTableOverview;
  onOpenVersion: (version: number) => void;
  /** The published material, looked up by id, for its name and file size. */
  materialFor?: (materialId: string) => LaunchMaterial | undefined;
  canManage?: boolean;
  onPublishNew?: () => void;
  /** Renames the material behind the version in force. */
  onRename?: (material: LaunchMaterial, title: string) => Promise<void>;
}) {
  const { t } = useTranslation('features/launches');

  // The other five categories draw an empty state; a lone grey sentence here
  // read as a panel that failed to load, on the one category a broker opens
  // first.
  if (!overview.current) {
    return (
      <div
        data-testid="price-table-empty"
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 'var(--space-2)',
          padding: 'var(--space-12) var(--space-4)',
          borderRadius: 'var(--radius-lg)',
          border: '1px dashed var(--border-medium)',
          textAlign: 'center',
        }}
      >
        <span style={{ color: 'var(--text-tertiary)', display: 'flex' }}>
          <Table2 size={28} strokeWidth={1.5} />
        </span>

        <Text
          as="span"
          style={{
            fontSize: 'var(--text-body)',
            fontWeight: 'var(--font-semibold)',
            color: 'var(--text-primary)',
          }}
        >
          {t('priceTable.emptyTitle')}
        </Text>

        <Text
          as="span"
          style={{
            fontSize: 'var(--text-small)',
            color: 'var(--text-secondary)',
            maxWidth: '46ch',
          }}
        >
          {canManage ? t('priceTable.emptyBodyManager') : t('priceTable.emptyBodyBroker')}
        </Text>

        {canManage && onPublishNew && (
          <button
            type="button"
            onClick={onPublishNew}
            style={{
              marginTop: 'var(--space-2)',
              height: 'var(--space-10)',
              padding: '0 var(--space-5)',
              borderRadius: 'var(--radius-md)',
              border: 'none',
              background: 'var(--button-primary-bg)',
              color: 'var(--button-primary-text)',
              fontSize: 'var(--text-body-sm)',
              fontWeight: 'var(--font-semibold)',
              cursor: 'pointer',
            }}
          >
            {t('materials.add')}
          </button>
        )}
      </div>
    );
  }

  const current = overview.current;
  const currentMaterial = materialFor?.(current.material_id);
  const currentSize = formatSize(currentMaterial?.size_bytes);

  return (
    <div
      data-testid="price-table-panel"
      style={{
        display: 'grid',
        gridTemplateColumns: 'minmax(0,1.2fr) minmax(0,1fr)',
        gap: 'var(--space-4)',
        alignItems: 'start',
      }}
    >
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--space-4)',
          padding: 'var(--space-5)',
          borderRadius: 'var(--radius-lg)',
          border: '1px solid var(--border-subtle)',
          background: 'var(--surface-raised)',
          boxShadow: 'var(--shadow-md)',
        }}
      >
        {/* The headline is the name the manager gave the table, not its
            version: "v1" is bookkeeping this product invented, and a user
            with no feel for it cannot tell "v1" from "v2" at a glance the
            way they can tell "Tabela outubro/26" from "Tabela lançamento".
            The number survives as a tag, because the stale-table warning
            still speaks in versions. */}
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 'var(--space-3)' }}>
          <div
            style={{
              flex: 1,
              minWidth: 0,
              display: 'flex',
              flexDirection: 'column',
              gap: 'var(--space-1)',
            }}
          >
            <Text as="span" style={{ fontSize: 'var(--text-small)', color: 'var(--text-secondary)' }}>
              {t('priceTable.currentVersionTitle')}
            </Text>

            {currentMaterial ? (
              <MaterialTitle
                material={currentMaterial}
                canRename={canManage}
                onRename={onRename}
                t={t}
              />
            ) : (
              <Text
                as="span"
                style={{
                  fontSize: 'var(--text-xl)',
                  fontWeight: 'var(--font-semibold)',
                  color: 'var(--text-primary)',
                }}
              >
                {t('priceTable.untitled')}
              </Text>
            )}
          </div>

          <span
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--space-1-5)',
              flexShrink: 0,
            }}
          >
            <Text
              as="span"
              style={{
                fontSize: 'var(--text-meta)',
                fontWeight: 'var(--font-semibold)',
                padding: 'var(--space-1) var(--space-2)',
                borderRadius: 'var(--radius-sm)',
                background: 'var(--launch-price-current-bg)',
                color: 'var(--launch-price-current-fg)',
                lineHeight: 1,
              }}
            >
              {t('priceTable.currentBadge')}
            </Text>

            <Text
              as="span"
              style={{
                fontSize: 'var(--text-meta)',
                fontWeight: 'var(--font-semibold)',
                padding: 'var(--space-1) var(--space-2)',
                borderRadius: 'var(--radius-sm)',
                background: 'var(--surface-tertiary)',
                color: 'var(--text-secondary)',
                lineHeight: 1,
              }}
            >
              {t('priceTable.versionTag', { version: current.version })}
            </Text>
          </span>
        </div>

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3,1fr)',
            gap: 'var(--space-3)',
            paddingTop: 'var(--space-3)',
            borderTop: '1px solid var(--border-subtle)',
          }}
        >
          <Fact label={t('priceTable.validFromLabel')} value={shortDate(current.valid_from)} />
          <Fact label={t('priceTable.publishedByLabel')} value={current.published_by} />
          <Fact
            label={t('priceTable.fileLabel')}
            value={[currentMaterial ? 'PDF' : '', currentSize].filter(Boolean).join(' · ')}
          />
        </div>

        <div style={{ display: 'flex', gap: 'var(--space-2)', flexWrap: 'wrap' }}>
          <button
            type="button"
            onClick={() => onOpenVersion(current.version)}
            style={{
              height: 'var(--space-10)',
              padding: '0 var(--space-4)',
              borderRadius: 'var(--radius-md)',
              border: 'none',
              background: 'var(--button-primary-bg)',
              color: 'var(--button-primary-text)',
              fontSize: 'var(--text-body-sm)',
              fontWeight: 'var(--font-semibold)',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--space-2)',
            }}
          >
            <Eye size={16} />
            {t('priceTable.openTable', { version: current.version })}
          </button>

          {canManage && onPublishNew && (
            <button
              type="button"
              onClick={onPublishNew}
              style={{
                height: 'var(--space-10)',
                padding: '0 var(--space-4)',
                borderRadius: 'var(--radius-md)',
                border: '1px solid var(--button-secondary-border)',
                background: 'var(--button-secondary-bg)',
                color: 'var(--button-secondary-text)',
                fontSize: 'var(--text-body-sm)',
                fontWeight: 'var(--font-semibold)',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--space-2)',
              }}
            >
              <Upload size={16} />
              {t('priceTable.publishNew')}
            </button>
          )}
        </div>
      </div>

      {overview.history.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
          <Text
            as="span"
            style={{
              fontSize: 'var(--text-body)',
              fontWeight: 'var(--font-semibold)',
              color: 'var(--text-primary)',
            }}
          >
            {t('priceTable.archiveTitle')}
          </Text>
          <Text as="span" style={{ fontSize: 'var(--text-small)', color: 'var(--text-secondary)' }}>
            {t('priceTable.archiveWarning')}
          </Text>

          <div
            style={{
              borderRadius: 'var(--radius-lg)',
              border: '1px solid var(--border-subtle)',
              background: 'var(--surface-card)',
              overflow: 'hidden',
              marginTop: 'var(--space-1)',
            }}
          >
            {overview.history.map((version) => (
              <ArchivedRow
                key={version.price_table_id}
                version={version}
                name={materialFor?.(version.material_id)?.title ?? ''}
                size={formatSize(materialFor?.(version.material_id)?.size_bytes)}
                seen={overview.seen_version === version.version}
                onOpen={() => onOpenVersion(version.version)}
                t={t}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
